import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";

import type { LoomCommandAcceptance } from "./loomAuthoringHistory";
import { canonicalLoomDefinitionJson } from "./loomAcceptedDefinitionBinding";
import type { LoomAuthoringCommand, LoomAuthoringDocument, LoomPrimitiveDefinition } from "./loomAuthoringModel";
import { parseStoredLoomControlSurfacePoseReceipt, serializeLoomControlSurfacePoseReceipt } from "./loomControlSurfacePersistence";
import type { LoomControlSurfaceAcceptedGeometry } from "../../api/loomControlSurfaceRebuild";
import { useLoomControlSurfaceGeometry } from "./useLoomControlSurfaceGeometry";
import {
  createDefaultLoomWingControlSurface,
  createLoomWingControlSurfacePose,
  controlSurfaceIdForBodyId,
  deriveLoomWingControlSurfaceDefinitionRevision,
  deriveLoomWingControlSurfacePoseRevision,
  deriveLoomWingDefinitionRevision,
  parseLoomWingControlSurfaceCollection,
  parseLoomWingControlSurfacePose,
  updateLoomWingControlSurfacePose,
  type LoomControlSurfaceActuationMode,
  type LoomControlSurfaceSide,
  type LoomSha256Revision,
  type LoomWingControlSurfaceCollection,
  type LoomWingControlSurfaceDefinition,
  type LoomWingControlSurfacePose,
} from "./loomWingControlSurfaceContract";

type WingNode = Extract<LoomAuthoringDocument["nodes"][number], { type: "primitive" }> & {
  definition: Extract<LoomPrimitiveDefinition, { primitive: "wing" }>;
};

export interface LoomControlSurfaceHealth {
  motion: "cached-rigid-transform";
  gap: "sealed" | "positive";
  gapAuthority: "nominal-ratio-three-section-samples/v1";
  collision: "no-overlap-in-sampled-sections" | "collision" | "unavailable";
  hinge: "straight-3d-resolved";
  fidelity: "adapted-sampled-section-loft";
  validationAuthority: "ephemeral-server-evaluation";
  hingeToleranceM: number;
  maxIntermediateDeviationM: number;
  minimumGapM: number;
  endGapM: number;
}

export interface LoomControlSurfaceEvaluation {
  readonly status: "unavailable" | "checking" | "adapted" | "error";
  readonly message: string | null;
  readonly requestedDefinitionRevision: LoomSha256Revision | null;
  readonly acceptedDefinitionRevision: LoomSha256Revision | null;
  readonly requestedPoseRevision: LoomSha256Revision | null;
  readonly acceptedPoseRevision: LoomSha256Revision | null;
  readonly staleDropCount: number;
  readonly health: LoomControlSurfaceHealth | null;
}

export interface LoomControlSurfaceContextValue {
  readonly canAddControlSurface: boolean;
  readonly collection: LoomWingControlSurfaceCollection | null;
  readonly definitionRevision: LoomSha256Revision | null;
  readonly pose: LoomWingControlSurfacePose | null;
  readonly poseRevision: LoomSha256Revision | null;
  readonly evaluation: LoomControlSurfaceEvaluation;
  readonly acceptedGeometry?: LoomControlSurfaceAcceptedGeometry | null;
  readonly selectedBodyId: string | null;
  readonly selectedControl: LoomWingControlSurfaceDefinition | null;
  readonly selectedState: LoomWingControlSurfacePose["states"][number] | null;
  readonly selectedWing: WingNode | null;
  readonly persistenceState: "ephemeral" | "persisted" | "blocked";
  readonly document: LoomAuthoringDocument;
  readonly error: string | null;
  addControlSurface(): Promise<void>;
  replaceControlSurface(control: LoomWingControlSurfaceDefinition): void;
  reportViewerReady(): void;
  reportViewerError(message: string): void;
  selectBody(bodyId: string): void;
  selectControlSurface(controlSurfaceId: string): void;
  setActuationMode(mode: LoomControlSurfaceActuationMode): void;
  setCoupledDeflection(commandDeg: number): void;
  setIndependentDeflection(side: LoomControlSurfaceSide, angleDeg: number): void;
  setResolvedDeflections(
    mode: LoomControlSurfaceActuationMode,
    portDeflectionDeg: number,
    starboardDeflectionDeg: number,
  ): void;
}

interface LoomControlSurfaceProviderProps {
  children: ReactNode;
  document: LoomAuthoringDocument;
  onCommand: (command: LoomAuthoringCommand, acceptance?: LoomCommandAcceptance) => string | null;
  projectId: string;
  selectedNodeId: string;
}

const Context = createContext<LoomControlSurfaceContextValue | null>(null);

export function loomControlSurfacePoseStorageKey(projectId: string, wingId: string): string {
  return `loom-wing-control-surface-pose:v1:${encodeURIComponent(projectId)}:${encodeURIComponent(wingId)}`;
}

function selectedWing(document: LoomAuthoringDocument, selectedNodeId: string): WingNode | null {
  const node = document.nodes.find(({ id }) => id === selectedNodeId);
  return node?.type === "primitive" && node.definition.primitive === "wing" ? node as WingNode : null;
}

function neutralWingDefinition(wing: WingNode): Omit<WingNode["definition"], "controlSurfaces"> {
  const { controlSurfaces: _controls, ...neutral } = wing.definition;
  return neutral;
}

function unsupportedWingOccurrencePlacement(document: LoomAuthoringDocument, wingId: string): string | null {
  const byId = new Map(document.nodes.map((node) => [node.id, node])); const reachable = new Set<string>();
  const visit = (id: string) => {
    if (reachable.has(id)) return; reachable.add(id);
    const node = byId.get(id);
    if (node?.type === "transform") visit(node.inputNodeId);
    else if (node?.type === "boolean") node.inputNodeIds.forEach(visit);
  };
  document.rootNodeIds.forEach(visit);
  const dependsOnWing = (id: string, seen = new Set<string>()): boolean => {
    if (id === wingId) return true;
    if (seen.has(id)) return false; seen.add(id);
    const node = byId.get(id);
    if (node?.type === "transform") return dependsOnWing(node.inputNodeId, seen);
    if (node?.type === "boolean") return node.inputNodeIds.some((input) => dependsOnWing(input, seen));
    return false;
  };
  let exactMirrorOccurrence = false;
  for (const node of document.nodes) {
    if (node.type !== "transform" || !reachable.has(node.id) || !dependsOnWing(node.inputNodeId)) continue;
    const canonicalMirror = node.inputNodeId === wingId && node.operation.transform === "mirror"
      && node.operation.axis === "y" && node.operation.planeOffsetM === 0;
    if (!canonicalMirror) return `Control-surface v1 is unavailable: authored occurrence placement ${node.id} (${node.operation.transform}) is not supported`;
    exactMirrorOccurrence = true;
  }
  return exactMirrorOccurrence ? null
    : "Control-surface v1 is unavailable: an exact reflected mirror occurrence is required for port and starboard fixed geometry";
}

function availableBand(collection: LoomWingControlSurfaceCollection | null): { start: number; end: number } {
  if (!collection?.controlSurfaces.length) return { start: 0.55, end: 0.9 };
  const edges = [0, ...collection.controlSurfaces.flatMap(({ spanBand }) => [spanBand.start, spanBand.end]), 1];
  let best = { start: 0, end: 0, width: -1 };
  for (let index = 0; index + 1 < edges.length; index += 2) {
    const start = edges[index]! + 0.02;
    const end = edges[index + 1]! - 0.02;
    if (end - start > best.width) best = { start, end, width: end - start };
  }
  if (best.width < 1e-4) throw new Error("No unambiguous span band remains for another control surface");
  const width = Math.min(0.35, best.width);
  return { start: Number(best.start.toFixed(6)), end: Number((best.start + width).toFixed(6)) };
}

function nextControlId(collection: LoomWingControlSurfaceCollection | null): string {
  let index = 1;
  while (collection?.controlSurfaces.some(({ id }) => id === `control-${index}`)) index += 1;
  return `control-${index}`;
}

function rebindPose(
  collection: LoomWingControlSurfaceCollection,
  definitionRevision: LoomSha256Revision,
  previous: LoomWingControlSurfacePose | null,
  preferredSelection: string | null,
): LoomWingControlSurfacePose {
  const byId = new Map(previous?.states.map((state) => [state.controlSurfaceId, state]) ?? []);
  const selected = preferredSelection && collection.controlSurfaces.some(({ id }) => id === preferredSelection)
    ? preferredSelection
    : previous?.selectedControlSurfaceId && collection.controlSurfaces.some(({ id }) => id === previous.selectedControlSurfaceId)
      ? previous.selectedControlSurfaceId : collection.controlSurfaces[0]?.id ?? null;
  const neutral = createLoomWingControlSurfacePose(collection, definitionRevision, selected);
  const states = neutral.states.map((state) => byId.get(state.controlSurfaceId) ?? state);
  try {
    return parseLoomWingControlSurfacePose({ ...neutral, states }, collection, definitionRevision);
  } catch {
    return neutral;
  }
}

export function LoomControlSurfaceProvider({
  children, document, onCommand, projectId, selectedNodeId,
}: LoomControlSurfaceProviderProps) {
  const wing = useMemo(() => selectedWing(document, selectedNodeId), [document, selectedNodeId]);
  const placementError = useMemo(() => wing
    ? unsupportedWingOccurrencePlacement(document, wing.id) : null, [document, wing]);
  const authoredCollection = wing?.definition.controlSurfaces ?? null;
  const authoredCollectionIdentity = authoredCollection
    ? canonicalLoomDefinitionJson(authoredCollection) : null;
  const stableCollectionRef = useRef<{ identity: string | null; value: LoomWingControlSurfaceCollection | null }>({
    identity: null, value: null,
  });
  if (stableCollectionRef.current.identity !== authoredCollectionIdentity) {
    stableCollectionRef.current = { identity: authoredCollectionIdentity, value: authoredCollection };
  }
  const collection = stableCollectionRef.current.value;
  const definitionInputIdentity = wing ? canonicalLoomDefinitionJson({
    schema: "loom.control-surface-definition-input/v1", wingId: wing.id,
    neutralWingDefinition: neutralWingDefinition(wing), controlSurfaceCollection: collection,
  }) : null;
  const [wingDefinitionRevision, setWingDefinitionRevision] = useState<LoomSha256Revision | null>(null);
  const [definitionRevision, setDefinitionRevision] = useState<LoomSha256Revision | null>(null);
  const [pose, setPose] = useState<LoomWingControlSurfacePose | null>(null);
  const [poseRevision, setPoseRevision] = useState<LoomSha256Revision | null>(null);
  const [selectedBodyId, setSelectedBodyId] = useState<string | null>(null);
  const [persistenceState, setPersistenceState] = useState<"ephemeral" | "persisted" | "blocked">("ephemeral");
  const [error, setError] = useState<string | null>(null);
  const [viewerError, setViewerError] = useState<string | null>(null);
  const revisionRequestRef = useRef(0);
  const restoreRequestRef = useRef(0);
  const preferredSelectionRef = useRef<string | null>(null);
  const definitionCollectionRef = useRef<string | null>(null);
  const revisionInputRef = useRef({ collection, wing });
  revisionInputRef.current = { collection, wing };
  const persistenceRequestRef = useRef(0);
  const poseRequestRef = useRef(0);

  useEffect(() => {
    const request = ++revisionRequestRef.current;
    const { collection, wing } = revisionInputRef.current;
    definitionCollectionRef.current = null;
    setWingDefinitionRevision(null);
    setDefinitionRevision(null);
    setError(null);
    if (!wing) return;
    if (placementError) { setError(placementError); return; }
    if (wing.definition.tip !== "flat" || wing.definition.tipRadiusM !== 0) {
      setError("Control-surface v1 is unavailable: the neutral wing must have a flat, zero-radius tip");
      return;
    }
    void deriveLoomWingDefinitionRevision(wing.id, neutralWingDefinition(wing)).then(async (wingRevision) => {
      if (request !== revisionRequestRef.current) return;
      setWingDefinitionRevision(wingRevision);
      if (!collection) return;
      if (collection.wingDefinitionRevision !== wingRevision) throw new Error("Control-surface definition is stale for the neutral wing definition");
      const nextRevision = await deriveLoomWingControlSurfaceDefinitionRevision(collection);
      if (request !== revisionRequestRef.current) return;
      definitionCollectionRef.current = definitionInputIdentity;
      setDefinitionRevision(nextRevision);
    }).catch((reason: unknown) => {
      if (request === revisionRequestRef.current) setError(reason instanceof Error ? reason.message : "Control-surface definition is unavailable");
    });
  }, [definitionInputIdentity, placementError]);

  useEffect(() => {
    const request = ++restoreRequestRef.current;
    if (!collection || !definitionRevision || definitionCollectionRef.current !== definitionInputIdentity) {
      setPose(null); setPoseRevision(null); setPersistenceState("ephemeral");
      return;
    }
    const raw = localStorage.getItem(loomControlSurfacePoseStorageKey(projectId, collection.wingId));
    if (raw === null) {
      const preferred = preferredSelectionRef.current;
      setPose((current) => rebindPose(collection, definitionRevision, current, preferred));
      if (preferredSelectionRef.current === preferred) preferredSelectionRef.current = null;
      setError(null);
      return;
    }
    void parseStoredLoomControlSurfacePoseReceipt(raw, collection, definitionRevision).then((restored) => {
      if (request !== restoreRequestRef.current) return;
      const preferred = preferredSelectionRef.current;
      setPose(restored.staleDefinition || preferred
        ? rebindPose(collection, definitionRevision, restored.pose, preferred)
        : restored.pose);
      if (preferredSelectionRef.current === preferred) preferredSelectionRef.current = null;
      setError(null);
    }).catch((reason: unknown) => {
      if (request !== restoreRequestRef.current) return;
      const preferred = preferredSelectionRef.current;
      setError(reason instanceof Error ? `Stored control pose unavailable: ${reason.message}` : "Stored control pose unavailable");
      setPose((current) => rebindPose(collection, definitionRevision, current, preferred));
      if (preferredSelectionRef.current === preferred) preferredSelectionRef.current = null;
    });
  }, [collection, definitionInputIdentity, definitionRevision, projectId]);

  useEffect(() => {
    setSelectedBodyId((current) => {
      if (!current || !collection) return null;
      if (current === `${collection.wingId}:fixed`) {
        return pose?.selectedControlSurfaceId === null ? current : null;
      }
      return controlSurfaceIdForBodyId(collection, current) === pose?.selectedControlSurfaceId ? current : null;
    });
  }, [collection, pose?.selectedControlSurfaceId]);

  useEffect(() => {
    const request = ++poseRequestRef.current;
    setPoseRevision(null);
    if (!pose) return;
    if (!collection || !definitionRevision) return;
    void deriveLoomWingControlSurfacePoseRevision(collection, pose).then((nextRevision) => {
      if (request === poseRequestRef.current) setPoseRevision(nextRevision);
      else return;
    }).catch((reason: unknown) => {
      if (request === poseRequestRef.current) setError(reason instanceof Error ? reason.message : "Control-surface pose receipt is unavailable");
    });
  }, [collection, definitionRevision, pose]);

  useEffect(() => {
    if (!collection || !definitionRevision || !pose || !poseRevision
      || pose.definitionRevision !== definitionRevision) return;
    const request = ++persistenceRequestRef.current;
    setPersistenceState("ephemeral");
    void serializeLoomControlSurfacePoseReceipt(collection, pose).then((raw) => {
      if (request !== persistenceRequestRef.current) return;
      const receiptRevision = (JSON.parse(raw) as { poseRevision?: unknown }).poseRevision;
      if (receiptRevision !== poseRevision) return;
      localStorage.setItem(loomControlSurfacePoseStorageKey(projectId, collection.wingId), raw);
      setPersistenceState("persisted");
    }).catch((reason: unknown) => {
      if (request !== persistenceRequestRef.current) return;
      setPersistenceState("blocked");
      setError(reason instanceof Error ? reason.message : "Control-surface pose persistence is blocked");
    });
  }, [collection, definitionRevision, pose, poseRevision, projectId]);

  const geometry = useLoomControlSurfaceGeometry({
    collection, definitionRevision, document, projectId, wing: wing?.definition ?? null,
  });
  const acceptedHealth = geometry.accepted?.health.find(({ controlSurfaceId }) => (
    controlSurfaceId === pose?.selectedControlSurfaceId
  ));
  const acceptedHinge = geometry.accepted?.hinges.find(({ controlSurfaceId }) => (
    controlSurfaceId === pose?.selectedControlSurfaceId
  ));
  const evaluation = useMemo<LoomControlSurfaceEvaluation>(() => ({
    status: viewerError || geometry.status === "error" ? "error"
      : geometry.status === "updating" ? "checking"
      : geometry.status === "accepted" ? "adapted" : "unavailable",
    message: viewerError ?? geometry.error,
    requestedDefinitionRevision: geometry.requestedDefinitionRevision,
    acceptedDefinitionRevision: geometry.accepted?.definitionRevision ?? null,
    requestedPoseRevision: poseRevision,
    acceptedPoseRevision: geometry.accepted ? poseRevision : null,
    staleDropCount: geometry.staleDropCount,
    health: acceptedHealth && acceptedHinge ? {
      motion: "cached-rigid-transform", gap: acceptedHealth.minimumGapM > 0 ? "positive" : "sealed",
      gapAuthority: "nominal-ratio-three-section-samples/v1",
      collision: acceptedHealth.status === "unavailable" ? "unavailable"
        : acceptedHealth.collides ? "collision" : "no-overlap-in-sampled-sections",
      hinge: "straight-3d-resolved", fidelity: "adapted-sampled-section-loft",
      validationAuthority: "ephemeral-server-evaluation", hingeToleranceM: acceptedHinge.toleranceM,
      maxIntermediateDeviationM: acceptedHinge.maximumDeviationM,
      minimumGapM: acceptedHealth.minimumGapM, endGapM: acceptedHealth.endGapM,
    } : null,
  }), [acceptedHealth, acceptedHinge, geometry, poseRevision, viewerError]);

  const replaceCollection = (next: LoomWingControlSurfaceCollection): string | null => {
    if (!wing) return "Select a Loom wing first";
    const command: LoomAuthoringCommand = {
      schema: "loom-authoring-command/v1", commandId: `gui-control-surface-${document.revision + 1}`,
      expectedRevision: document.revision, kind: "primitive.replace", nodeId: wing.id,
      definition: { ...neutralWingDefinition(wing), controlSurfaces: next },
    };
    return onCommand(command, { origin: "gui.control", subjectIds: [wing.id, ...next.controlSurfaces.map(({ id }) => id)] });
  };
  const addControlSurface = async () => {
    if (!wing || !wingDefinitionRevision) { setError("Neutral wing revision is still unavailable"); return; }
    try {
      const id = nextControlId(collection); const spanBand = availableBand(collection);
      const control = createDefaultLoomWingControlSurface(id, `Control surface ${id.slice(id.lastIndexOf("-") + 1)}`, spanBand);
      const next = parseLoomWingControlSurfaceCollection({
        schema: "loom.wing-control-surface-collection/v1", wingId: wing.id, wingDefinitionRevision,
        controlSurfaces: [...(collection?.controlSurfaces ?? []), control],
      });
      restoreRequestRef.current += 1;
      preferredSelectionRef.current = id;
      const commandError = replaceCollection(next);
      if (commandError) throw new Error(commandError);
      setError(null);
    } catch (reason) {
      preferredSelectionRef.current = null;
      setError(reason instanceof Error ? reason.message : "Unable to add control surface");
    }
  };
  const replaceControlSurface = (control: LoomWingControlSurfaceDefinition) => {
    if (!collection) return;
    try {
      const next = parseLoomWingControlSurfaceCollection({
        ...collection,
        controlSurfaces: collection.controlSurfaces.map((candidate) => candidate.id === control.id ? control : candidate),
      });
      restoreRequestRef.current += 1;
      preferredSelectionRef.current = pose?.selectedControlSurfaceId ?? null;
      const commandError = replaceCollection(next);
      if (commandError) throw new Error(commandError);
      setError(null);
    } catch (reason) {
      preferredSelectionRef.current = null;
      setError(reason instanceof Error ? reason.message : "Control-surface definition is invalid");
    }
  };
  const updatePose = (updater: (current: LoomWingControlSurfacePose) => LoomWingControlSurfacePose) => {
    // Apply via the functional setState form (not `updater(pose)` against the render's
    // closed-over `pose`), so this update is queued behind any pose update already in
    // flight from this same render pass (e.g. the pose-restore effect settling a just-added
    // control surface) instead of racing it. `pose` can still read `null` here even though a
    // restore is already queued to make it non-null; queuing lets React apply updates in
    // the order they were requested rather than dropping ours because we observed `null`
    // a moment too early.
    setPose((current) => {
      if (!current) return current;
      try { const next = updater(current); setError(null); return next; }
      catch (reason) { setError(reason instanceof Error ? reason.message : "Control-surface pose is invalid"); return current; }
    });
  };
  const selectControlSurface = (id: string) => {
    restoreRequestRef.current += 1;
    preferredSelectionRef.current = id;
    setSelectedBodyId((current) => current && collection
      && controlSurfaceIdForBodyId(collection, current) === id ? current : null);
    updatePose((current) => collection && definitionRevision
      ? parseLoomWingControlSurfacePose({ ...current, selectedControlSurfaceId: id }, collection, definitionRevision)
      : current);
  };
  const selectBody = (bodyId: string) => {
    setSelectedBodyId(bodyId);
    if (!collection) return;
    const fixedId = `${collection.wingId}:fixed`;
    if (bodyId === fixedId) {
      restoreRequestRef.current += 1;
      preferredSelectionRef.current = null;
      updatePose((current) => definitionRevision
        ? parseLoomWingControlSurfacePose({ ...current, selectedControlSurfaceId: null }, collection, definitionRevision)
        : current);
    }
    else {
      const controlId = controlSurfaceIdForBodyId(collection, bodyId);
      if (controlId) selectControlSurface(controlId);
    }
  };
  const selectedControl = collection?.controlSurfaces.find(({ id }) => id === pose?.selectedControlSurfaceId) ?? null;
  const selectedState = pose?.states.find(({ controlSurfaceId }) => controlSurfaceId === selectedControl?.id) ?? null;
  const patchSelectedState = (update: Parameters<typeof updateLoomWingControlSurfacePose>[3]) => {
    if (!collection || !pose || !selectedControl) return;
    updatePose((current) => updateLoomWingControlSurfacePose(collection, current, selectedControl.id, update));
  };
  const value: LoomControlSurfaceContextValue = {
    canAddControlSurface: Boolean(wing && wingDefinitionRevision && !placementError),
    collection, definitionRevision, pose, poseRevision, evaluation, acceptedGeometry: geometry.accepted, selectedBodyId,
    selectedControl, selectedState, selectedWing: wing, persistenceState, document, error,
    addControlSurface, replaceControlSurface, selectBody, selectControlSurface,
    reportViewerReady: () => setViewerError(null),
    reportViewerError: (message) => {
      const fullMessage = `Control-surface viewer unavailable: ${message}`;
      setViewerError((current) => current === fullMessage ? current : fullMessage);
    },
    setActuationMode: (mode) => patchSelectedState({ mode }),
    setCoupledDeflection: (angle) => patchSelectedState({ starboardDeflectionDeg: angle }),
    setIndependentDeflection: (side, angle) => patchSelectedState(side === "port"
      ? { mode: "independent", portDeflectionDeg: angle }
      : { mode: "independent", starboardDeflectionDeg: angle }),
    setResolvedDeflections: (mode, portDeflectionDeg, starboardDeflectionDeg) => {
      if (mode === "symmetric" && portDeflectionDeg !== starboardDeflectionDeg) {
        setError("Symmetric resolved deflections must match exactly");
        return;
      }
      if (mode === "antisymmetric" && portDeflectionDeg !== -starboardDeflectionDeg) {
        setError("Antisymmetric resolved deflections must be exact opposites");
        return;
      }
      patchSelectedState({ mode, portDeflectionDeg, starboardDeflectionDeg });
    },
  };
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useLoomControlSurfaceContext(): LoomControlSurfaceContextValue | null {
  return useContext(Context);
}
